import React, { useEffect, useMemo, useState } from 'react';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthProvider';
import { branchAPI } from '../../services';
import AdminRooms from '../../components/admin/AdminRooms';
import { toast } from 'react-toastify';

const BranchSettingsPanel = () => {
  const { selectedBranch, refreshBranches, selectBranch } = useBranch();
  const { user } = useAuth();
  const branchId = selectedBranch?.id;

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [locating, setLocating] = useState(false);
  const [ipWhitelist, setIpWhitelist] = useState('');
  const [qrSecret, setQrSecret] = useState('');
  const [cashBalance, setCashBalance] = useState('');
  const [updatingCashBalance, setUpdatingCashBalance] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [orderFeeType, setOrderFeeType] = useState('VAT');
  const [surchargeThreshold, setSurchargeThreshold] = useState('30000');
  const [surchargePerItemAmount, setSurchargePerItemAmount] = useState('2000');
  const [savingOrderFee, setSavingOrderFee] = useState(false);
  const [telegramGroupUrl, setTelegramGroupUrl] = useState('');
  const [zaloGroupUrl, setZaloGroupUrl] = useState('');
  const [savingZaloGroupUrl, setSavingZaloGroupUrl] = useState(false);
  // Branch extra / payment OTP
  const [branchExtra, setBranchExtra] = useState({});
  const [paymentOtpEnabled, setPaymentOtpEnabled] = useState(false);
  const [paymentOtpRequireZalo, setPaymentOtpRequireZalo] = useState(false);
  const [paymentOtpSendBillZalo, setPaymentOtpSendBillZalo] = useState(false);
  const [savingPaymentOtp, setSavingPaymentOtp] = useState(false);
  // Booking OTP config (đặt bàn web)
  const [bookingOtpEnabled, setBookingOtpEnabled] = useState(true);
  const [savingBookingOtp, setSavingBookingOtp] = useState(false);
  // Menu Training config
  const [menuTrainingEnabled, setMenuTrainingEnabled] = useState(false);
  const [menuTrainingAutoLock, setMenuTrainingAutoLock] = useState(false);
  const [savingMenuTraining, setSavingMenuTraining] = useState(false);
  const [maxSeats, setMaxSeats] = useState('');
  const [savingTelegramCapacity, setSavingTelegramCapacity] = useState(false);
  const [phone, setPhone] = useState('');
  const [operatingHoursOpen, setOperatingHoursOpen] = useState('');
  const [operatingHoursClose, setOperatingHoursClose] = useState('');
  const [createData, setCreateData] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    location: { lat: '', lng: '' },
    is_active: true,
  });

  const canEdit = useMemo(() => !!branchId, [branchId]);

  useEffect(() => {
    if (!branchId) return;
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const res = await branchAPI.getBranchById(branchId);
        const data = res?.data || res;
        if (ignore) return;
        setName(data?.name || '');
        setCode(data?.code || '');
        setAddress(data?.address || '');
        setPhone(data?.phone || '');
        setOperatingHoursOpen(data?.operating_hours?.open || '');
        setOperatingHoursClose(data?.operating_hours?.close || '');
        setIsActive(data?.is_active !== undefined ? data.is_active : true);
        setIpWhitelist(Array.isArray(data?.ip_whitelist) ? data.ip_whitelist.join('\n') : '');
        setLat(data?.location?.lat ?? '');
        setLng(data?.location?.lng ?? '');
        setCashBalance(data?.current_cash_balance ?? '');
        setOrderFeeType(data?.order_fee_type ?? 'VAT');
        setSurchargeThreshold(String(data?.surcharge_threshold ?? 30000));
        setSurchargePerItemAmount(String(data?.surcharge_per_item_amount ?? 2000));
        setTelegramGroupUrl(data?.telegram_group_url ?? '');
        setMaxSeats(data?.max_seats != null ? String(data.max_seats) : '');
        const extra = (data?.extra && typeof data.extra === 'object') ? data.extra : {};
        setBranchExtra(extra);
        setPaymentOtpEnabled(extra?.payment_otp?.enabled ?? false);
        setPaymentOtpRequireZalo(extra?.payment_otp?.require_zalo_at_checkout ?? false);
        setPaymentOtpSendBillZalo(extra?.payment_otp?.send_bill_zalo ?? false);
        setBookingOtpEnabled(extra?.booking_otp?.enabled ?? true);
        setZaloGroupUrl(extra?.zalo_group_url ?? '');
        setMenuTrainingEnabled(extra?.menu_training?.enabled ?? false);
        setMenuTrainingAutoLock(extra?.menu_training?.auto_lock_menu ?? false);
      } catch (e) {
        toast.error('Không tải được thông tin chi nhánh');
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [branchId]);

  const handleSaveGeneral = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    try {
      setLoading(true);
      const loc = lat !== '' && lng !== '' ? { lat: parseFloat(lat), lng: parseFloat(lng) } : null;
      await branchAPI.updateBranch(branchId, {
        name,
        code,
        address,
        phone: phone.trim() || undefined,
        ...(loc ? { location: loc } : {}),
        ...(operatingHoursOpen && operatingHoursClose
          ? { operating_hours: { open: operatingHoursOpen, close: operatingHoursClose } }
          : {}),
      });
      toast.success('Đã lưu cấu hình chung');
      await refreshBranches();
    } catch (e) {
      toast.error('Lưu cấu hình chung thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIP = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    try {
      setLoading(true);
      const list = ipWhitelist
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      await branchAPI.updateBranch(branchId, { ip_whitelist: list });
      toast.success('Đã cập nhật IP whitelist');
    } catch (e) {
      toast.error('Cập nhật IP whitelist thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQR = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    try {
      setLoading(true);
      await branchAPI.updateBranch(branchId, { qr_code_secret: qrSecret });
      toast.success('Đã cập nhật QR secret');
    } catch (e) {
      toast.error('Cập nhật QR secret thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTelegramCapacity = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    const seats = maxSeats.trim() === '' ? null : parseInt(maxSeats, 10);
    if (maxSeats.trim() !== '' && (isNaN(seats) || seats < 1)) {
      toast.error('Số chỗ ngồi phải là số nguyên dương');
      return;
    }
    try {
      setSavingTelegramCapacity(true);
      await branchAPI.updateBranch(branchId, {
        telegram_group_url: telegramGroupUrl.trim() || null,
        max_seats: seats,
      });
      toast.success('Đã lưu cấu hình Telegram & sức chứa');
      await refreshBranches();
    } catch (err) {
      toast.error('Lưu thất bại');
    } finally {
      setSavingTelegramCapacity(false);
    }
  };

  const handleSaveZaloGroupUrl = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    try {
      setSavingZaloGroupUrl(true);
      const mergedExtra = {
        ...branchExtra,
        zalo_group_url: zaloGroupUrl.trim() || undefined,
      };
      await branchAPI.updateBranch(branchId, { extra: mergedExtra });
      setBranchExtra(mergedExtra);
      toast.success('Đã lưu link nhóm Zalo');
    } catch (err) {
      toast.error('Lưu link nhóm Zalo thất bại');
    } finally {
      setSavingZaloGroupUrl(false);
    }
  };

  const handleSaveOrderFee = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    const threshold = parseInt(surchargeThreshold, 10);
    const perItem = parseInt(surchargePerItemAmount, 10);
    if (orderFeeType === 'SURCHARGE' && (isNaN(threshold) || isNaN(perItem) || threshold < 0 || perItem < 0)) {
      toast.error('Ngưỡng và phụ thu phải là số không âm');
      return;
    }
    try {
      setSavingOrderFee(true);
      const payload = { order_fee_type: orderFeeType };
      if (orderFeeType === 'SURCHARGE') {
        payload.surcharge_threshold = threshold;
        payload.surcharge_per_item_amount = perItem;
      }
      await branchAPI.updateBranch(branchId, payload);
      toast.success('Đã lưu cấu hình phí đơn hàng');
      await refreshBranches();
    } catch (err) {
      toast.error('Lưu cấu hình phí đơn hàng thất bại');
    } finally {
      setSavingOrderFee(false);
    }
  };

  const handleSaveCashBalance = async (e) => {
    e?.preventDefault?.();
    if (!branchId || !user?.id) return;
    const balance = parseFloat(cashBalance);
    if (isNaN(balance)) {
      toast.error('Số dư tiền mặt không hợp lệ');
      return;
    }
    try {
      setUpdatingCashBalance(true);
      await branchAPI.updateCashBalance(branchId, { cash_balance: balance });
      toast.success('Đã cập nhật số dư tiền mặt');
      await refreshBranches();
    } catch (e) {
      toast.error(e?.message || 'Cập nhật số dư tiền mặt thất bại');
    } finally {
      setUpdatingCashBalance(false);
    }
  };

  const handleToggleActive = async () => {
    if (!branchId) return;
    try {
      setToggling(true);
      const newActiveState = !isActive;
      await branchAPI.updateBranch(branchId, { is_active: newActiveState });
      setIsActive(newActiveState);
      toast.success(`Đã ${newActiveState ? 'bật' : 'tắt'} chi nhánh`);
      await refreshBranches();
    } catch (e) {
      toast.error('Thay đổi trạng thái chi nhánh thất bại');
    } finally {
      setToggling(false);
    }
  };

  const handleCreateBranch = async (e) => {
    e?.preventDefault?.();
    try {
      setCreating(true);
      const payload = {
        name: createData.name,
        code: createData.code,
        address: createData.address,
        phone: createData.phone || undefined,
        is_active: createData.is_active,
      };
      if (createData.location.lat !== '' && createData.location.lng !== '') {
        payload.location = {
          lat: parseFloat(createData.location.lat),
          lng: parseFloat(createData.location.lng),
        };
      }
      const res = await branchAPI.createBranch(payload);
      const newBranch = res?.data || res;
      toast.success('Đã tạo chi nhánh mới');
      setShowCreate(false);
      setCreateData({ name: '', code: '', address: '', phone: '', location: { lat: '', lng: '' }, is_active: true });
      await refreshBranches();
      if (newBranch?.id) {
        selectBranch(newBranch);
      }
    } catch (e) {
      toast.error('Tạo chi nhánh thất bại');
    } finally {
      setCreating(false);
    }
  };

  const handleSavePaymentOtp = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    try {
      setSavingPaymentOtp(true);
      const mergedExtra = {
        ...branchExtra,
        payment_otp: {
          ...(branchExtra?.payment_otp ?? {}),
          enabled: paymentOtpEnabled,
          require_zalo_at_checkout: paymentOtpRequireZalo,
          send_bill_zalo: paymentOtpSendBillZalo,
        },
      };
      await branchAPI.updateBranch(branchId, { extra: mergedExtra });
      setBranchExtra(mergedExtra);
      toast.success('Đã lưu cấu hình bảo mật thanh toán');
    } catch (err) {
      toast.error('Lưu cấu hình bảo mật thanh toán thất bại');
    } finally {
      setSavingPaymentOtp(false);
    }
  };

  const handleSaveBookingOtp = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    try {
      setSavingBookingOtp(true);
      const mergedExtra = {
        ...branchExtra,
        booking_otp: {
          ...(branchExtra?.booking_otp ?? {}),
          enabled: bookingOtpEnabled,
        },
      };
      await branchAPI.updateBranch(branchId, { extra: mergedExtra });
      setBranchExtra(mergedExtra);
      toast.success('Đã lưu cấu hình OTP đặt bàn');
    } catch (err) {
      toast.error('Lưu cấu hình OTP đặt bàn thất bại');
    } finally {
      setSavingBookingOtp(false);
    }
  };

  const handleSaveMenuTraining = async (e) => {
    e?.preventDefault?.();
    if (!branchId) return;
    try {
      setSavingMenuTraining(true);
      const mergedExtra = {
        ...branchExtra,
        menu_training: {
          ...(branchExtra?.menu_training ?? {}),
          enabled: menuTrainingEnabled,
          auto_lock_menu: menuTrainingAutoLock,
        },
      };
      await branchAPI.updateBranch(branchId, { extra: mergedExtra });
      setBranchExtra(mergedExtra);
      toast.success('Đã lưu cấu hình đào tạo món');
    } catch (err) {
      toast.error('Lưu cấu hình đào tạo món thất bại');
    } finally {
      setSavingMenuTraining(false);
    }
  };

  const handleGetCurrentLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Trình duyệt không hỗ trợ định vị');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLat(String(latitude));
        setLng(String(longitude));
        toast.success('Đã lấy vị trí hiện tại');
        setLocating(false);
      },
      (err) => {
        toast.error('Không thể lấy vị trí hiện tại');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Cấu hình chi nhánh</h1>
            <p>Quản lý thông tin, IP whitelist và QR secret cho chi nhánh</p>
          </div>
        </div>

        {!canEdit && <div className="alert alert-warning">Vui lòng chọn chi nhánh để cấu hình.</div>}

        <div className="card mb-4">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Tạo chi nhánh mới</h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setShowCreate((s) => !s)}
            >
              {showCreate ? 'Ẩn' : 'Hiện'} form
            </button>
          </div>
          {showCreate && (
            <div className="card-body">
              <form onSubmit={handleCreateBranch}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Tên chi nhánh *</label>
                      <input
                        className="form-control"
                        value={createData.name}
                        onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Mã chi nhánh *</label>
                      <input
                        className="form-control"
                        value={createData.code}
                        onChange={(e) => setCreateData({ ...createData, code: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label">Địa chỉ</label>
                      <input
                        className="form-control"
                        value={createData.address}
                        onChange={(e) => setCreateData({ ...createData, address: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Số điện thoại</label>
                      <input
                        className="form-control"
                        value={createData.phone}
                        onChange={(e) => setCreateData({ ...createData, phone: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-6">
                    <div className="mb-3">
                      <label className="form-label">Vĩ độ (lat)</label>
                      <input
                        className="form-control"
                        value={createData.location.lat}
                        onChange={(e) =>
                          setCreateData({ ...createData, location: { ...createData.location, lat: e.target.value } })
                        }
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3">
                      <label className="form-label">Kinh độ (lng)</label>
                      <input
                        className="form-control"
                        value={createData.location.lng}
                        onChange={(e) =>
                          setCreateData({ ...createData, location: { ...createData.location, lng: e.target.value } })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={creating}
                  >
                    {creating ? 'Đang tạo...' : 'Tạo chi nhánh'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCreate(false)}
                  >
                    Đóng
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="row">
          <div className="col-lg-6">
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Thông tin chung</h5>
                {canEdit && (
                  <div className="d-flex align-items-center gap-2">
                    <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {isActive ? 'Đang hoạt động' : 'Đã tắt'}
                    </span>
                    <button
                      type="button"
                      className={`btn btn-sm ${isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                      onClick={handleToggleActive}
                      disabled={toggling || loading}
                    >
                      {toggling ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Đang xử lý...
                        </>
                      ) : isActive ? (
                        'Tắt chi nhánh'
                      ) : (
                        'Bật chi nhánh'
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveGeneral}>
                  <div className="mb-3">
                    <label className="form-label">Tên chi nhánh</label>
                    <input
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={!canEdit || loading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Mã chi nhánh</label>
                    <input
                      className="form-control"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      disabled={!canEdit || loading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Địa chỉ</label>
                    <input
                      className="form-control"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      disabled={!canEdit || loading}
                    />
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="mb-3">
                        <label className="form-label">Vĩ độ (lat)</label>
                        <input
                          className="form-control"
                          value={lat}
                          onChange={(e) => setLat(e.target.value)}
                          disabled={!canEdit || loading}
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="mb-3">
                        <label className="form-label">Kinh độ (lng)</label>
                        <input
                          className="form-control"
                          value={lng}
                          onChange={(e) => setLng(e.target.value)}
                          disabled={!canEdit || loading}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleGetCurrentLocation}
                      disabled={!canEdit || loading || locating}
                    >
                      {locating ? 'Đang lấy vị trí...' : 'Lấy vị trí hiện tại'}
                    </button>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Số điện thoại</label>
                    <input
                      className="form-control"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={!canEdit || loading}
                      placeholder="0367xxxxxx"
                    />
                    <small className="form-text text-muted d-block mt-1">
                      Hiển thị trên trang đặt bàn khi khách chọn chi nhánh.
                    </small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Giờ mở cửa</label>
                    <div className="row g-2">
                      <div className="col-6">
                        <input
                          type="time"
                          className="form-control"
                          value={operatingHoursOpen}
                          onChange={(e) => setOperatingHoursOpen(e.target.value)}
                          disabled={!canEdit || loading}
                        />
                        <small className="form-text text-muted">Mở cửa</small>
                      </div>
                      <div className="col-6">
                        <input
                          type="time"
                          className="form-control"
                          value={operatingHoursClose}
                          onChange={(e) => setOperatingHoursClose(e.target.value)}
                          disabled={!canEdit || loading}
                        />
                        <small className="form-text text-muted">Đóng cửa</small>
                      </div>
                    </div>
                    <small className="form-text text-muted d-block mt-1">
                      Dùng cho trang đặt bàn để sinh danh sách khung giờ.
                    </small>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canEdit || loading}
                  >
                    Lưu
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0">IP Whitelist</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveIP}>
                  <div className="mb-3">
                    <label className="form-label">Danh sách IP (mỗi dòng một IP)</label>
                    <textarea
                      className="form-control"
                      rows={8}
                      value={ipWhitelist}
                      onChange={(e) => setIpWhitelist(e.target.value)}
                      disabled={!canEdit || loading}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canEdit || loading}
                  >
                    Cập nhật
                  </button>
                </form>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0">QR Code Secret</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveQR}>
                  <div className="mb-3">
                    <label className="form-label">Secret</label>
                    <input
                      className="form-control"
                      value={qrSecret}
                      onChange={(e) => setQrSecret(e.target.value)}
                      disabled={!canEdit || loading}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canEdit || loading}
                  >
                    Lưu Secret
                  </button>
                </form>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Telegram & Sức chứa</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveTelegramCapacity}>
                  <div className="mb-3">
                    <label className="form-label">Link nhóm Telegram chi nhánh</label>
                    <input
                      type="url"
                      className="form-control"
                      value={telegramGroupUrl}
                      onChange={(e) => setTelegramGroupUrl(e.target.value)}
                      disabled={!canEdit || loading}
                      placeholder="https://t.me/+tNqFEKa6N80yNTFl"
                    />
                    <small className="form-text text-muted d-block mt-1">
                      Link mời join nhóm. Sau khi nhân viên liên kết Telegram sẽ hiển thị link này.
                    </small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Link nhóm Zalo chi nhánh</label>
                    <input
                      type="url"
                      className="form-control"
                      value={zaloGroupUrl}
                      onChange={(e) => setZaloGroupUrl(e.target.value)}
                      disabled={!canEdit || loading}
                      placeholder="https://zalo.me/g/..."
                    />
                    <small className="form-text text-muted d-block mt-1">
                      Link mời join nhóm Zalo. Hiển thị sau khi nhân viên liên kết Telegram thành công.
                    </small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Số chỗ ngồi tối đa</label>
                    <input
                      type="number"
                      min={1}
                      className="form-control"
                      value={maxSeats}
                      onChange={(e) => setMaxSeats(e.target.value)}
                      disabled={!canEdit || loading}
                      placeholder="Để trống = không giới hạn"
                    />
                    <small className="form-text text-muted d-block mt-1">
                      Sức chứa tối đa chi nhánh tiếp đón cùng lúc. Dùng cho đặt bàn/đặt chỗ.
                    </small>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!canEdit || loading || savingTelegramCapacity}
                    >
                      {savingTelegramCapacity ? 'Đang lưu...' : 'Lưu Telegram & Sức chứa'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleSaveZaloGroupUrl}
                      disabled={!canEdit || loading || savingZaloGroupUrl}
                    >
                      {savingZaloGroupUrl ? 'Đang lưu...' : 'Lưu link Zalo'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Phòng</h5>
              </div>
              <div className="card-body">
                <small className="form-text text-muted d-block mb-3">
                  Mỗi phòng sửa ngay tại nút bút chì trên thẻ phòng: tên, sức chứa, giá/giờ, số
                  khách tối thiểu, tiện ích, ảnh, hành động dọn và việc cho khách đặt trên app.
                </small>
                <AdminRooms hideTitle />
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Phí đơn hàng</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveOrderFee}>
                  <div className="mb-3">
                    <label className="form-label">Loại phí</label>
                    <select
                      className="form-select"
                      value={orderFeeType}
                      onChange={(e) => setOrderFeeType(e.target.value)}
                      disabled={!canEdit || loading}
                    >
                      <option value="VAT">Thuế (VAT %)</option>
                      <option value="SURCHARGE">Thuế và phí áp dụng (phụ thu theo món)</option>
                    </select>
                    <small className="form-text text-muted d-block mt-1">
                      {orderFeeType === 'VAT'
                        ? 'Áp dụng thuế VAT theo cấu hình (VAT_RATE).'
                        : 'Mỗi món có đơn giá ≥ ngưỡng sẽ cộng phụ thu theo số lượng.'}
                    </small>
                  </div>
                  {orderFeeType === 'SURCHARGE' && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Ngưỡng đơn giá (VNĐ)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={surchargeThreshold}
                          onChange={(e) => setSurchargeThreshold(e.target.value)}
                          disabled={!canEdit || loading}
                          min={0}
                        />
                        <small className="form-text text-muted d-block mt-1">
                          Món có đơn giá ≥ số này mới tính phụ thu.
                        </small>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Phụ thu mỗi đơn vị (VNĐ)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={surchargePerItemAmount}
                          onChange={(e) => setSurchargePerItemAmount(e.target.value)}
                          disabled={!canEdit || loading}
                          min={0}
                        />
                        <small className="form-text text-muted d-block mt-1">
                          Ví dụ: 2 ly ≥ 30k → cộng 2 × 2.000đ = 4.000đ.
                        </small>
                      </div>
                    </>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canEdit || loading || savingOrderFee}
                  >
                    {savingOrderFee ? 'Đang lưu...' : 'Lưu cấu hình phí'}
                  </button>
                </form>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">🔐 Bảo mật thanh toán (OTP)</h5>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Khi bật, nhân viên cần gửi OTP cho khách hàng qua Zalo và nhập mã xác nhận từ khách
                  trước khi hoàn tất thanh toán. Ngăn chặn gian lận xóa món sau khi thu tiền.
                </p>
                <form onSubmit={handleSavePaymentOtp}>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="paymentOtpEnabled"
                        checked={paymentOtpEnabled}
                        onChange={(e) => setPaymentOtpEnabled(e.target.checked)}
                        disabled={!canEdit || loading}
                      />
                      <label className="form-check-label" htmlFor="paymentOtpEnabled">
                        Bật xác thực OTP khi thanh toán
                      </label>
                    </div>
                    <small className="form-text text-muted d-block mt-1">
                      Nếu tắt, thanh toán hoạt động như bình thường (không cần OTP).
                    </small>
                  </div>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="paymentOtpRequireZalo"
                        checked={paymentOtpRequireZalo}
                        onChange={(e) => setPaymentOtpRequireZalo(e.target.checked)}
                        disabled={!canEdit || loading || !paymentOtpEnabled}
                      />
                      <label className="form-check-label" htmlFor="paymentOtpRequireZalo">
                        Yêu cầu số điện thoại có Zalo khi đặt hàng
                      </label>
                    </div>
                    <small className="form-text text-muted d-block mt-1">
                      Khách không có Zalo sẽ không thể đặt hàng từ menu app (cần nhân viên nhập thủ công).
                    </small>
                  </div>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="paymentOtpSendBillZalo"
                        checked={paymentOtpSendBillZalo}
                        onChange={(e) => setPaymentOtpSendBillZalo(e.target.checked)}
                        disabled={!canEdit || loading}
                      />
                      <label className="form-check-label" htmlFor="paymentOtpSendBillZalo">
                        Gửi hoá đơn "Thanh toán thành công" qua Zalo cho khách
                      </label>
                    </div>
                    <small className="form-text text-muted d-block mt-1">
                      Mặc định TẮT. Zalo bot hay bị chặn/phạt khi nhắn người lạ — chỉ bật nếu chi nhánh cần.
                      Khách vẫn nhận thông báo realtime trên thiết bị đã đặt đơn.
                    </small>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canEdit || loading || savingPaymentOtp}
                  >
                    {savingPaymentOtp ? 'Đang lưu...' : 'Lưu cấu hình bảo mật'}
                  </button>
                </form>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">📅 Xác thực OTP đặt bàn (web)</h5>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Khi bật, khách tự đặt bàn từ menu app phải xác thực OTP gửi qua Zalo.
                  Nếu Zalo không gửi được nhưng số có Zalo hợp lệ, hệ thống tự bỏ qua OTP cho khách đặt.
                  Nếu số chưa có Zalo, khách được báo lỗi để nhập số khác.
                </p>
                <form onSubmit={handleSaveBookingOtp}>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="bookingOtpEnabled"
                        checked={bookingOtpEnabled}
                        onChange={(e) => setBookingOtpEnabled(e.target.checked)}
                        disabled={!canEdit || loading}
                      />
                      <label className="form-check-label" htmlFor="bookingOtpEnabled">
                        Bật xác thực OTP khi đặt bàn
                      </label>
                    </div>
                    <small className="form-text text-muted d-block mt-1">
                      Nếu tắt, khách đặt bàn không cần OTP (đặt bàn thành công ngay).
                    </small>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canEdit || loading || savingBookingOtp}
                  >
                    {savingBookingOtp ? 'Đang lưu...' : 'Lưu cấu hình OTP đặt bàn'}
                  </button>
                </form>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">📚 Đào tạo món (Menu Training)</h5>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Theo dõi nhân viên đã biết làm món nào. Khi bật, nhân viên sẽ nhận nhắc nhở
                  học món mới khi check-in ca. Tùy chọn tự động ẩn món trên menu khi
                  không có nhân viên biết làm đang trong ca.
                </p>
                <form onSubmit={handleSaveMenuTraining}>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="menuTrainingEnabled"
                        checked={menuTrainingEnabled}
                        onChange={(e) => setMenuTrainingEnabled(e.target.checked)}
                        disabled={!canEdit || loading}
                      />
                      <label className="form-check-label" htmlFor="menuTrainingEnabled">
                        Bật tính năng đào tạo món
                      </label>
                    </div>
                    <small className="form-text text-muted d-block mt-1">
                      Khi bật: nhân viên thấy tab "Món mới" trong trang Đào tạo, nhận
                      Telegram nhắc học khi check-in, và có thể submit bằng chứng để được đồng nghiệp xác nhận.
                    </small>
                  </div>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="menuTrainingAutoLock"
                        checked={menuTrainingAutoLock}
                        onChange={(e) => setMenuTrainingAutoLock(e.target.checked)}
                        disabled={!canEdit || loading || !menuTrainingEnabled}
                      />
                      <label className="form-check-label" htmlFor="menuTrainingAutoLock">
                        Tự động ẩn món khi không có nhân viên biết làm trong ca
                      </label>
                    </div>
                    <small className="form-text text-muted d-block mt-1">
                      ⚠️ Chỉ bật sau khi đã chạy backfill seed nhân viên cũ. Nếu bật quá sớm,
                      món sẽ bị ẩn ngay khi không ai CONFIRMED trong ca hiện tại.
                    </small>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canEdit || loading || savingMenuTraining}
                  >
                    {savingMenuTraining ? 'Đang lưu...' : 'Lưu cấu hình đào tạo'}
                  </button>
                </form>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Số dư tiền mặt</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveCashBalance}>
                  <div className="mb-3">
                    <label className="form-label">Số dư tiền mặt (VNĐ)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={cashBalance}
                      onChange={(e) => setCashBalance(e.target.value)}
                      disabled={!canEdit || updatingCashBalance}
                      placeholder="Nhập số dư tiền mặt"
                    />
                    {cashBalance && (
                      <small className="form-text text-muted">
                        {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                          parseFloat(cashBalance) || 0,
                        )}
                      </small>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canEdit || updatingCashBalance}
                  >
                    {updatingCashBalance ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" />
                        Đang cập nhật...
                      </>
                    ) : (
                      'Cập nhật số dư'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default BranchSettingsPanel;
